import React, { useEffect, useMemo, useRef, useState } from 'react'

import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import { DropdownSearchMode } from 'src/public/components/toast_local/common/location_dropdown/LocationDropdown'

import config from 'config'
import { t } from 'src/translations'

interface Location {
  lat: string
  long: string
}

interface Props {
  searchLocation?: Location
  mode: DropdownSearchMode
}

const CateringDiscover = ({ searchLocation, mode }: Props) => {
  const shouldShow = mode !== 'Restaurants'
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [didLoadiFrame, setDidLoadiFrame] = useState(false)

  // Initialize query params only once so that we don't reload the iframe when search changes
  const queryParams = useMemo(() => {
    return new URLSearchParams({
      embedded: 'true',
      utm_source: 'toast_local_embedded',
      mode: mode.toLowerCase(),
      ...searchLocation
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [updatedSearch, setUpdatedSearch] = useState<Location | undefined>(
    searchLocation
  )

  useEffect(() => {
    if (didLoadiFrame && shouldShow && updatedSearch) {
      try {
        const message = { type: 'locationUpdate', ...updatedSearch }
        iframeRef.current?.contentWindow?.postMessage(
          message,
          config.resources.toastwebBaseUri
        )
      } catch (error) {
        console.error('Error posting location message to iframe:', error)
      }
    }
  }, [updatedSearch, didLoadiFrame, shouldShow])

  // Post message to iframe when mode changes
  useEffect(() => {
    if (didLoadiFrame && shouldShow) {
      try {
        const message = { type: 'modeChange', mode: mode.toLowerCase() }
        iframeRef.current?.contentWindow?.postMessage(
          message,
          config.resources.toastwebBaseUri
        )
      } catch (error) {
        console.error('Error posting mode change to iframe:', error)
      }
    }
  }, [mode, didLoadiFrame, shouldShow])

  // Update the search location in the iframe only if the catering page is showing
  // and when the value coming is actually different than the previous request
  useEffect(() => {
    const latChanged =
      searchLocation?.lat && searchLocation?.lat !== updatedSearch?.lat
    const longChanged =
      searchLocation?.long && searchLocation?.long !== updatedSearch?.long
    if (shouldShow && (latChanged || longChanged)) {
      setUpdatedSearch(searchLocation)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLocation, shouldShow])

  useEffect(() => {
    document.body.style.overflow = shouldShow ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [shouldShow])

  if (!shouldShow && !didLoadiFrame) {
    return null
  }

  return (
    <div className='catering-container'>
      {!didLoadiFrame && (
        <div
          className='catering-loading-overlay'
          data-testid='catering-loading-spinner'
        >
          <LoadingSpinner color={'#C0C0C0'} strokeWidth={1} />
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={t('catering-discover.catering-discover')}
        className='catering-iframe'
        data-testid='catering-iframe'
        style={{ display: shouldShow ? 'inline' : 'none' }}
        tabIndex={-1}
        onLoad={() => setDidLoadiFrame(true)}
        src={`${
          config.resources.toastwebBaseUri
        }catering/locations/discover?${queryParams.toString()}`}
      />
    </div>
  )
}

export default CateringDiscover
